import AttachFile from "@mui/icons-material/AttachFile";
import AutoAwesome from "@mui/icons-material/AutoAwesome";
import Delete from "@mui/icons-material/Delete";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import Download from "@mui/icons-material/Download";
import UploadFile from "@mui/icons-material/UploadFile";
import Visibility from "@mui/icons-material/Visibility";
import { Box, Button, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { isPreviewable } from "components/Document/shared/fileTypeUtils";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import {
  DocumentType,
  ProcessedManifestDocumentsQuery,
  WasteManifestSource,
} from "generated-graphql/graphql";

import { SourceLetterBox } from "../SourceLetterBox";
import { SectionHeader } from "./SectionHeader";
import { ManifestPdfDocument } from "./types";

/** Synthetic grid row id for the "no manifest document" empty-state row. */
const MISSING_MANIFEST_ROW_ID = "__missing-manifest-document__";

/** Document types that represent a manifest PDF (processable into manifest data). */
const MANIFEST_DOCUMENT_TYPES = new Set<DocumentType>([
  DocumentType.RcraManifest,
  DocumentType.RcraManifestPrintedCopy,
]);

/**
 * Manifest-PDF row label, derived from the document's type rather than its slot
 * — so a doc that became the data source via a switch (e.g. an RCRAInfo printed
 * copy) is labeled correctly instead of always reading "User Uploaded".
 */
function manifestPdfLabel(documentType: DocumentType | null | undefined): string {
  return documentType === DocumentType.RcraManifestPrintedCopy
    ? "Manifest (RCRAInfo)"
    : "Manifest (User Uploaded)";
}

type SupportingDoc = NonNullable<
  ProcessedManifestDocumentsQuery["processedManifests"]["items"][number]["documents"]
>[number];

type ExceptionReportDoc = NonNullable<
  NonNullable<
    ProcessedManifestDocumentsQuery["processedManifests"]["items"][number]["exceptionReportSubmission"]
  >["documents"]
>[number];

interface DocumentsSectionProps {
  uploadedPdf: ManifestPdfDocument | null | undefined;
  rcraInfoApiPdf?: ManifestPdfDocument | null;
  supportingDocuments: SupportingDoc[] | undefined;
  exceptionReportDocuments: ExceptionReportDoc[] | undefined;
  documentsError: boolean;
  showExceptionReporting: boolean;
  onAttach: () => void;
  onPreviewSupporting: (doc: { id: string; title: string; fileExtension?: string | null }) => void;
  onDownload: (documentId: string) => void;
  onDetach: (manifestDocumentId: string) => void;
  onViewPdf: (document: ManifestPdfDocument) => void;
  /**
   * Document id the manifest data (document pillar) was extracted from. That row
   * gets the source-document dot and the review/edit-manifest-data action.
   */
  sourceDocumentId?: string | null;
  /** True once manifest data has been extracted (labels the action Edit vs View). */
  manifestDataExtracted?: boolean;
  /** The source document is flagged needs-review (warns the action). */
  manifestDocumentNeedsReview?: boolean;
  /** Whether a manifest source document exists; drives the missing-document row. */
  hasManifestDocument?: boolean;
  /** Staff + flag gate for the manifest-document affordances. */
  canManageManifestDocument?: boolean;
  /**
   * Whether the detach action is offered. Staff-only for now (the detach
   * mutation also allows facility-permissioned customers, but we keep the
   * affordance staff-only until that flow is product-ready). Attach, view, and
   * download remain available to all users.
   */
  canDetach?: boolean;
  /** Open the review/edit dialog for the manifest source document. */
  onReviewManifestDocument?: () => void;
  /** Open the upload dialog from the missing-document row. */
  onUploadManifestDocument?: () => void;
  /** Process a non-source manifest document into the manifest data (switch source). */
  onProcessManifestDocument?: (documentId: string, documentName: string) => void;
}

interface DocumentRow {
  id: string;
  /** The underlying Document id (distinct from the grid row id for supporting docs). */
  documentId: string;
  documentType?: DocumentType | null;
  typeLabel: string;
  name: string;
  fileExtension?: string | null;
  /** Open in the preview rail (manifest PDFs) or full-screen (supporting docs). */
  onView?: () => void;
  onDownload?: () => void;
  /** Detach is only offered for manifest-attached docs, not submission docs. */
  onDetach?: () => void;
  /** Marks the synthetic "no manifest document" empty-state row. */
  missing?: boolean;
}

/**
 * Manifest documents as a single grid that grows as documents are added — one
 * row per manifest PDF (origin-labeled) and per supporting document (Proof of
 * Contact, Exception Report, Other). Source-agnostic.
 */
export function DocumentsSection({
  uploadedPdf,
  rcraInfoApiPdf,
  supportingDocuments,
  exceptionReportDocuments,
  documentsError,
  showExceptionReporting,
  onAttach,
  onPreviewSupporting,
  onDownload,
  onDetach,
  onViewPdf,
  sourceDocumentId,
  manifestDataExtracted,
  manifestDocumentNeedsReview,
  hasManifestDocument,
  canManageManifestDocument,
  canDetach,
  onReviewManifestDocument,
  onUploadManifestDocument,
  onProcessManifestDocument,
}: DocumentsSectionProps) {
  const docs = supportingDocuments ?? [];

  const manifestPdfRows: DocumentRow[] = [
    ...(uploadedPdf
      ? [
          {
            id: uploadedPdf.id,
            documentId: uploadedPdf.id,
            documentType: uploadedPdf.documentType,
            typeLabel: manifestPdfLabel(uploadedPdf.documentType),
            name: uploadedPdf.title,
            fileExtension: uploadedPdf.fileExtension,
            onView: () => onViewPdf(uploadedPdf),
            onDownload: () => onDownload(uploadedPdf.id),
          },
        ]
      : []),
    // Skip the RCRAInfo row when it's the same Document as the uploaded/source
    // PDF (e.g. after switching the data source to the printed copy) — two grid
    // rows with the same id would make MUI DataGrid reject the whole set.
    ...(rcraInfoApiPdf && rcraInfoApiPdf.id !== uploadedPdf?.id
      ? [
          {
            id: rcraInfoApiPdf.id,
            documentId: rcraInfoApiPdf.id,
            documentType: rcraInfoApiPdf.documentType,
            typeLabel: manifestPdfLabel(rcraInfoApiPdf.documentType),
            name: rcraInfoApiPdf.title,
            fileExtension: rcraInfoApiPdf.fileExtension,
            onView: () => onViewPdf(rcraInfoApiPdf),
            onDownload: () => onDownload(rcraInfoApiPdf.id),
          },
        ]
      : []),
  ];

  const supportingRow = (d: SupportingDoc, typeLabel: string): DocumentRow => ({
    id: d.id,
    documentId: d.document.id,
    documentType: d.document.documentType,
    typeLabel,
    name: d.document.title,
    fileExtension: d.document.fileExtension,
    onView: isPreviewable(d.document.fileExtension)
      ? () => onPreviewSupporting(d.document)
      : undefined,
    onDownload: () => onDownload(d.document.id),
    onDetach: () => onDetach(d.id),
  });

  const supportingRows: DocumentRow[] = [
    ...docs
      .filter((d) => d.document.documentType === DocumentType.WasteTransporterContactProof)
      .map((d) => supportingRow(d, "45-Day Proof of Contact")),
    ...(showExceptionReporting
      ? docs
          .filter((d) => d.document.documentType === DocumentType.WasteExceptionReportProof)
          .map((d) => supportingRow(d, "60-Day Exception Report"))
      : []),
    ...(showExceptionReporting
      ? (exceptionReportDocuments ?? []).map((d) => ({
          id: d.id,
          documentId: d.document.id,
          documentType: d.document.documentType,
          typeLabel: "60-Day Exception Report",
          name: d.document.title,
          fileExtension: d.document.fileExtension,
          onView: isPreviewable(d.document.fileExtension)
            ? () => onPreviewSupporting(d.document)
            : undefined,
          onDownload: () => onDownload(d.document.id),
        }))
      : []),
    ...docs
      .filter(
        (d) =>
          d.document.documentType !== DocumentType.WasteTransporterContactProof &&
          d.document.documentType !== DocumentType.WasteExceptionReportProof &&
          // Manifest documents (uploaded or the RCRAInfo printed copy) are
          // surfaced as their own "Manifest" row above — never as "Other".
          !MANIFEST_DOCUMENT_TYPES.has(d.document.documentType)
      )
      .map((d) => supportingRow(d, "Other")),
  ];

  // Empty-state row: when there's no manifest document, surface a full-width
  // "no manifest document" row with an Upload action (in place of the old
  // header button) rather than silently omitting it.
  const showMissingRow = !!canManageManifestDocument && !hasManifestDocument;
  const missingRow: DocumentRow = {
    id: MISSING_MANIFEST_ROW_ID,
    documentId: MISSING_MANIFEST_ROW_ID,
    typeLabel: "",
    name: "",
    missing: true,
  };

  // Always sits as the last row, after any real documents.
  const rows = [...manifestPdfRows, ...supportingRows, ...(showMissingRow ? [missingRow] : [])];

  const reviewActionLabel = manifestDocumentNeedsReview
    ? "Review manifest document data"
    : manifestDataExtracted
      ? "Edit manifest document data"
      : "View document";

  const columns: CustomGridColDef<DocumentRow>[] = [
    {
      field: "typeLabel",
      headerName: "Type",
      width: 200,
      resizable: false,
      // The missing-document row spans the full grid: message on the left,
      // Upload action on the right.
      colSpan: (_, row) => (row.missing ? 4 : undefined),
      renderCell: ({ row }) => {
        if (row.missing) {
          const missingText = rcraInfoApiPdf
            ? "No processed manifest document — upload a manifest or process the manifest from RCRAInfo"
            : "No manifest document uploaded";
          return (
            <Stack direction="row" spacing={1} alignItems="center" width="100%" height="100%">
              {/* Text flexes + ellipsizes; the icon and button never shrink, so the
                  button stays fully visible as the drawer/rail narrows the cell. */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                <DescriptionOutlined
                  fontSize="small"
                  sx={{ color: "text.disabled", flexShrink: 0 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  noWrap
                  title={missingText}
                  // pr gives the italic slant room so the trailing glyph isn't
                  // clipped by the noWrap overflow:hidden when the text fits.
                  sx={{ fontStyle: "italic", minWidth: 0, pr: "2px" }}
                >
                  {rcraInfoApiPdf ? (
                    <>
                      No{" "}
                      <Box component="span" sx={{ fontWeight: 700 }}>
                        processed
                      </Box>{" "}
                      manifest document — upload a manifest or process the manifest from RCRAInfo
                    </>
                  ) : (
                    "No manifest document uploaded"
                  )}
                </Typography>
              </Stack>
              <Button
                size="small"
                color="warning"
                startIcon={<UploadFile fontSize="small" />}
                onClick={onUploadManifestDocument}
                sx={{ flexShrink: 0 }}
              >
                Upload manifest document
              </Button>
            </Stack>
          );
        }
        const isSource = sourceDocumentId != null && row.documentId === sourceDocumentId;
        return (
          <Stack direction="row" spacing={0.75} alignItems="center">
            {/* Fixed-width slot (sized to the letter box) keeps every Type
                label aligned; the box shows only on the row the manifest data
                is sourced from. */}
            <Box sx={{ width: 15, flexShrink: 0, display: "flex" }}>
              {isSource && (
                <SourceLetterBox
                  source={WasteManifestSource.ManifestDocument}
                  tooltip="Data source document"
                />
              )}
            </Box>
            <Typography variant="body2">{row.typeLabel}</Typography>
          </Stack>
        );
      },
    },
    {
      // The only flexing column — it absorbs all width changes as the drawer /
      // rail resize; the others are pinned.
      field: "name",
      headerName: "Document",
      flex: 1,
      minWidth: 160,
    },
    {
      field: "fileExtension",
      headerName: "Format",
      width: 90,
      resizable: false,
      renderCell: ({ row }) =>
        row.fileExtension ? (
          <Chip
            label={row.fileExtension.toUpperCase()}
            size="small"
            variant="outlined"
            sx={{ my: "auto" }}
          />
        ) : (
          "—"
        ),
    },
    {
      field: "actions",
      headerName: "",
      // Pinned to just fit the (up to three) action icons — no stray space,
      // and never small enough to clip an icon.
      width: 124,
      minWidth: 124,
      maxWidth: 124,
      sortable: false,
      resizable: false,
      renderCell: ({ row }) => {
        // The missing row spans all columns, so its Actions cell isn't rendered.
        if (row.missing) return null;
        const isSource = sourceDocumentId != null && row.documentId === sourceDocumentId;
        const isManifestDocument =
          row.documentType != null && MANIFEST_DOCUMENT_TYPES.has(row.documentType);
        // A manifest PDF that isn't the current data source can be processed
        // (run through doc intelligence) to become / switch the data source.
        const canProcess =
          !!canManageManifestDocument &&
          !!onProcessManifestDocument &&
          isManifestDocument &&
          !isSource;
        return (
          // Stop row-click (preview toggle) from firing when an action is clicked.
          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            height="100%"
            onClick={(e) => e.stopPropagation()}
          >
            {row.onView && (
              <IconButton size="small" onClick={row.onView} title="Preview">
                <Visibility fontSize="small" />
              </IconButton>
            )}
            {row.onDownload && (
              <IconButton size="small" onClick={row.onDownload} title="Download">
                <Download fontSize="small" />
              </IconButton>
            )}
            {isSource && canManageManifestDocument && onReviewManifestDocument && (
              <Tooltip title={reviewActionLabel}>
                <IconButton
                  size="small"
                  onClick={onReviewManifestDocument}
                  color="warning"
                  aria-label={reviewActionLabel}
                >
                  <DescriptionOutlined fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {canProcess && (
              <Tooltip title="Process manifest document data">
                <IconButton
                  size="small"
                  color="warning"
                  onClick={() => onProcessManifestDocument?.(row.documentId, row.name)}
                  aria-label="Process manifest document data"
                >
                  <AutoAwesome fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {row.onDetach && canDetach && (
              <IconButton size="small" onClick={row.onDetach} title="Detach">
                <Delete fontSize="small" />
              </IconButton>
            )}
          </Stack>
        );
      },
    },
  ];

  return (
    <Box>
      <SectionHeader
        title="Documents"
        action={
          <Button startIcon={<AttachFile />} size="small" onClick={onAttach}>
            Attach Document
          </Button>
        }
      >
        <InfoIconTooltip
          tooltip={
            "Only one document can be the active manifest document data source at a time. " +
            "Additional manifest copies and other associated documents (proof of vendor contact, " +
            "exception reports, etc.) can still be uploaded and attached."
          }
          fontSize="1rem"
          iconButtonSx={{ p: 0 }}
        />
      </SectionHeader>

      {documentsError && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          Failed to load documents
        </Typography>
      )}

      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(row) => row.id}
        disableRowSelectionOnClick
        hideFooter
        autoHeight
        localeText={{ noRowsLabel: "No documents yet" }}
        // Click a row to preview it (manifest PDFs toggle the side rail open/
        // closed; supporting docs open full-screen). Action buttons stop
        // propagation so they don't double-trigger.
        onRowClick={({ row }) => row.onView?.()}
        sx={{ "& .MuiDataGrid-row": { cursor: "pointer" } }}
      />
    </Box>
  );
}
